import React, { useCallback, useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, CalendarClock, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { PaginationControls, ResultsCounter } from 'shared';

import { PurchaseOrderActionsCell } from './PurchaseOrderActionsCell';
import { PurchaseOrderSortConfig } from './PurchaseOrderFilterConfig';
import { PurchaseOrderStatusCell } from './PurchaseOrderStatusCell';
import {
    formatPurchaseOrderDate,
    formatPurchaseOrderDateTime,
    isMidnightPurchaseOrderDateTime,
} from './utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatBuyerNames,
    isCrossDockPurchaseOrder,
    isLatePurchaseOrder,
    isPriorityPurchaseOrder,
} from './utils/purchaseOrderDisplayUtils';

const renderDeliveryCell = ({ getValue, row }) => {
    const rawValue = getValue();
    const formatted = formatPurchaseOrderDateTime(rawValue);
    const hasAppointment = !isMidnightPurchaseOrderDateTime(rawValue);
    const isLate = isLatePurchaseOrder(row?.original, rawValue);

    let iconColor = 'text-amber-500';
    let srLabel = 'Delivery time not scheduled';

    if (hasAppointment) {
        iconColor = 'text-emerald-600';
        srLabel = 'Delivery appointment scheduled';
    }

    if (isLate) {
        iconColor = 'text-red-600';
        srLabel = 'Delivery time late, appointment not scheduled';
    }

    return (
        <span className="inline-flex items-center gap-1">
            {formatted}
            <CalendarClock className={`h-4 w-4 ${iconColor}`} aria-hidden="true" />
            <span className="sr-only">{srLabel}</span>
        </span>
    );
};

const renderStatusCell = ({ getValue, row }) => (
    <PurchaseOrderStatusCell
        status={getValue()}
        fallbackText={FALLBACK_TEXT}
        isCrossDock={isCrossDockPurchaseOrder(row.original)}
        isPriority={isPriorityPurchaseOrder(row.original)}
    />
);

const renderActionsCell = ({ row, table }) => {
    const {
        onCancelPurchaseOrder,
        onDownloadCsv,
        onDownloadPdf,
        onDownloadShipList,
        onDuplicatePurchaseOrder,
        onViewPurchaseOrder,
    } = table.options.meta ?? {};
    const isSendToVendorDisabled = table.options.meta?.isSendToVendorDisabled;
    const onSendToVendor = table.options.meta?.onSendToVendor;
    const lastSentAt = formatPurchaseOrderDateTime(row.original?.sentToVendor, '') || undefined;
    return (
        <PurchaseOrderActionsCell
            onViewClick={() => onViewPurchaseOrder?.(row.original)}
            onCancel={() => onCancelPurchaseOrder?.(row.original)}
            onDownloadCsv={() => onDownloadCsv?.(row.original)}
            onDownloadPdf={() => onDownloadPdf?.(row.original)}
            onDownloadShipList={() => onDownloadShipList?.(row.original)}
            onDuplicate={() => onDuplicatePurchaseOrder?.(row.original)}
            onSendToVendor={() => onSendToVendor?.(row.original)}
            isSendToVendorDisabled={isSendToVendorDisabled?.(row.original)}
            lastSentAt={lastSentAt}
        />
    );
};

/**
 * Memoized row component to prevent unnecessary re-renders.
 */
const MemoizedPurchaseOrderRow = React.memo(({ row }) => (
    <TableRow data-state={row.getIsSelected() ? 'selected' : undefined}>
        {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

/**
 * Renders a TanStack-powered table for the Purchase Orders V2 page.
 * Supports prev/next pagination.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} [props.data=[]] - Purchase order rows.
 * @param {number} [props.totalCount=0] - Total number of matching records from the server.
 * @param {boolean} [props.isLoading=false] - Initial loading state.
 * @param {boolean} [props.isFetching=false] - Whether any fetch is in progress.
 * @param {boolean} [props.hasNextPage=false] - Whether there is a next page (paginated mode).
 * @param {boolean} [props.hasPrevious=false] - Whether there is a previous page (paginated mode).
 * @param {() => void} [props.onPrevious] - Callback to go to previous page (paginated mode).
 * @param {() => void} [props.onNext] - Callback to go to next page (paginated mode).
 * @param {(purchaseOrder: Object) => void} [props.onCancelPurchaseOrder] - Callback to cancel a PO.
 * @param {(purchaseOrder: Object) => void} [props.onDuplicatePurchaseOrder] - Callback to duplicate a PO.
 * @param {(purchaseOrder: Object) => void} [props.onDownloadCsv] - Callback to download CSV.
 * @param {(purchaseOrder: Object) => void} [props.onDownloadPdf] - Callback to download PDF.
 * @param {(purchaseOrder: Object) => void} [props.onDownloadShipList] - Callback to download ship list.
 * @param {(purchaseOrder: Object) => void} [props.onSendToVendor] - Callback to send to vendor.
 * @param {(purchaseOrder: Object) => boolean} [props.isSendToVendorDisabled] - Guardrail for sending to vendor.
 * @param {number} [props.currentPage] - Current page number (paginated mode).
 * @param {number} [props.totalPages] - Total number of pages (paginated mode).
 * @param {number} [props.pageSize] - Current page size for the page size selector.
 * @param {(size: number) => void} [props.onPageSizeChange] - Callback when page size changes.
 * @param {number[]} [props.pageSizeOptions] - Available page size options.
 * @param {Array<{id: string, desc: boolean}>} [props.sorting=[]] - TanStack table sorting state.
 * @param {(updater: Function|Array) => void} [props.onSortingChange] - Callback when sort changes.
 * @param {string} [props.emptyMessage='No purchase orders found.'] - Message when table is empty.
 * @returns {JSX.Element} Purchase orders data table.
 */
export function PurchaseOrdersTable({
    currentPage,
    data = [],
    emptyMessage = 'No purchase orders found.',
    hasNextPage = false,
    hasPrevious = false,
    isFetching = false,
    isLoading = false,
    isSendToVendorDisabled,
    onCancelPurchaseOrder,
    onDownloadCsv,
    onDownloadPdf,
    onDownloadShipList,
    onDuplicatePurchaseOrder,
    onNext,
    onPageSizeChange,
    onPrevious,
    onSendToVendor,
    onSortingChange,
    pageSize,
    pageSizeOptions,
    sorting,
    totalCount = 0,
    totalPages,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const history = useHistory();

    /**
     * Navigates to the purchase order entity view for the selected row,
     * preserving the current query string so filter state can be restored.
     */
    const handleViewPurchaseOrder = useCallback(
        (purchaseOrder) => {
            const purchaseOrderId = purchaseOrder?.id;
            const currentFilters =
                (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ?? '';

            history.push({
                pathname: purchaseOrderId
                    ? `/procurement/viewPurchaseOrder-v2/${purchaseOrderId}`
                    : '/procurement/viewPurchaseOrder-v2/unknown',
                state: {
                    purchaseOrder: purchaseOrder ?? null,
                    purchaseOrdersFilters: currentFilters,
                },
            });
        },
        [history],
    );

    const columns = useMemo(() => {
        const isSortable = (key) => Boolean(PurchaseOrderSortConfig.sortableFields[key]);
        return [
            {
                header: 'Facility',
                accessorKey: 'facilityName',
                enableSorting: false,
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'PO Number',
                accessorKey: 'id',
                enableSorting: isSortable('id'),
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'Created On',
                accessorKey: 'createdAt',
                enableSorting: isSortable('createdAt'),
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue()),
            },
            {
                header: 'Status',
                accessorKey: 'status',
                enableSorting: false,
                cell: renderStatusCell,
            },
            {
                header: 'Vendor',
                accessorKey: 'vendorName',
                enableSorting: false,
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'Ship Date',
                accessorFn: (row) => row?.pickupDate ?? row?.sentToVendor,
                enableSorting: false,
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue()),
            },
            {
                header: 'Scheduled Delivery',
                accessorKey: 'scheduledDeliveryDate',
                enableSorting: false,
                cell: renderDeliveryCell,
            },
            {
                id: 'buyerNames',
                header: 'Buyer Name(s)',
                accessorFn: (row) => row?.buyerNames ?? row?.buyers,
                enableSorting: false,
                cell: ({ getValue }) => formatBuyerNames(getValue()),
            },
            {
                id: 'produceOrGrocery',
                header: 'Produce / Grocery',
                accessorFn: (row) => row?.produceOrGrocery ?? row?.department,
                enableSorting: false,
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: renderActionsCell,
            },
        ];
    }, []);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        state: {
            sorting: sorting ?? [],
        },
        onSortingChange,
        meta: {
            onCancelPurchaseOrder,
            onDuplicatePurchaseOrder,
            onDownloadCsv,
            onDownloadPdf,
            onDownloadShipList,
            onSendToVendor,
            isSendToVendorDisabled,
            onViewPurchaseOrder: handleViewPurchaseOrder,
        },
    });

    const loadedCount = tableData.length;
    const isRefreshing = isFetching;

    return (
        <div className="flex flex-1 flex-col gap-2">
            {/* Results header using shared component */}
            {!isLoading && totalCount > 0 && (
                <div className="px-4 py-2">
                    <ResultsCounter loadedCount={loadedCount} totalCount={totalCount} isRefreshing={isRefreshing} />
                </div>
            )}

            <div className="flex min-h-[320px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading purchase orders…</span>
                        </div>
                    </div>
                ) : (
                    <div className="pb-6">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const canSort = header.column.getCanSort();
                                            const sorted = header.column.getIsSorted();
                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    className={`whitespace-normal break-words${canSort ? ' cursor-pointer select-none' : ''}`}
                                                    onClick={
                                                        canSort ? header.column.getToggleSortingHandler() : undefined
                                                    }
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <div className="flex items-center gap-1">
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext(),
                                                            )}
                                                            {canSort &&
                                                                (() => {
                                                                    if (sorted === 'asc')
                                                                        return <ArrowUp className="h-4 w-4 shrink-0" />;
                                                                    if (sorted === 'desc')
                                                                        return (
                                                                            <ArrowDown className="h-4 w-4 shrink-0" />
                                                                        );
                                                                    return (
                                                                        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                                    );
                                                                })()}
                                                        </div>
                                                    )}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => <MemoizedPurchaseOrderRow key={row.id} row={row} />)
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {/* Pagination section */}
                        {tableData.length > 0 && (
                            <PaginationControls
                                hasPrevious={hasPrevious}
                                hasNext={hasNextPage}
                                onPrevious={onPrevious}
                                onNext={onNext}
                                isLoading={isFetching}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                onPageSizeChange={onPageSizeChange}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
